import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import usePreferStaticUI from "@/lib/usePreferStaticUI";
import Reveal from "@/components/common/Reveal";
import SceneBackdrop from "@/components/motion/SceneBackdrop";
import SectionDivider from "@/components/motion/SectionDivider";
import HoverGlow from "@/components/motion/HoverGlow";
import MagneticButton from "@/components/motion/MagneticButton";
export { VisualTileGrid } from "./VisualTileGrid";

export const VisualSection = ({ children, testId, className = "", divider, ambient, tight }) => (
  <>
    {divider && <SectionDivider />}
    <section
      className={`relative wit-section-wide ${tight ? "py-8 lg:py-10" : "py-10 lg:py-14"} ${className}`}
      data-testid={testId}
    >
      {ambient && (
        <SceneBackdrop
          variant={ambient}
          sparkleCount={5}
          paths={false}
          smoke={ambient === "trust"}
          className="absolute inset-0 -z-10 opacity-75"
        />
      )}
      {children}
    </section>
  </>
);

export const VisualBanner = ({ title, subtitle, testId }) => {
  const reduced = usePreferStaticUI();

  return (
    <section className="wit-section-wide py-10 lg:py-12 relative" data-testid={testId}>
      <Reveal>
        <HoverGlow className="relative overflow-hidden rounded-3xl">
          <SceneBackdrop
            variant="trust"
            sparkleCount={6}
            paths={false}
            smoke
            className="absolute inset-0 -z-10 rounded-3xl"
          />
          <div className="wit-glass-strong p-8 sm:p-12 text-center relative">
            <h2 className="wit-heading-section mb-3">{title}</h2>
            {subtitle && (
              <p className="wit-body max-w-2xl mx-auto">{subtitle}</p>
            )}
            {!reduced && (
              <motion.div
                className="mx-auto mt-8 h-px max-w-xs bg-gradient-to-r from-transparent via-plum/30 to-transparent"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.2 }}
              />
            )}
          </div>
        </HoverGlow>
      </Reveal>
    </section>
  );
};

export const VisualCta = ({ to, label, testId }) => (
  <section className="wit-section-wide pb-14 lg:pb-16" data-testid={testId}>
    <Reveal>
      <div className="flex justify-center">
        <MagneticButton as={Link} to={to} className="wit-btn-primary">
          {label} <ArrowRight size={16} />
        </MagneticButton>
      </div>
    </Reveal>
  </section>
);

export const VisualMomentsGrid = ({ images = [], testId }) => {
  const reduced = usePreferStaticUI();

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid={testId}>
      {images.map((src, i) => (
        <Reveal key={i} delay={i * 0.04}>
          <motion.div
            className="wit-bento-cell p-0 overflow-hidden aspect-square group"
            whileHover={reduced ? undefined : { y: -4 }}
            transition={{ duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
          >
            <img
              src={src}
              alt=""
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              loading="lazy"
            />
          </motion.div>
        </Reveal>
      ))}
    </div>
  );
};
